import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { P2PHeader, P2PPage } from "@/components/p2p/P2PSubNav";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const STATUS_TABS = [
  { key: "all", label: "All", value: null },
  { key: "in_progress", label: "Active", value: "in_progress" },
  { key: "paid_marked", label: "Awaiting release", value: "paid_marked" },
  { key: "completed", label: "Completed", value: "completed" },
  { key: "cancelled", label: "Cancelled", value: "cancelled" },
  { key: "disputed", label: "Disputed", value: "disputed" },
];

const STATUS = {
  in_progress: { label: "Active", tone: "bg-primary/15 text-primary" },
  paid_marked: { label: "Paid", tone: "bg-warning/15 text-warning" },
  completed: { label: "Completed", tone: "bg-up/[0.12] text-up" },
  cancelled: { label: "Cancelled", tone: "bg-down/[0.12] text-down" },
  disputed: { label: "Disputed", tone: "bg-down/[0.12] text-down" },
  refunded: { label: "Refunded", tone: "bg-surface-2 text-muted-foreground" },
};

const fmtINR = (v) => Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const fmtDate = (s) =>
  s ? new Date(s).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";

export default function P2POrders() {
  const { user } = useAuth();
  const [tab, setTab] = useState("all");
  const [orders, setOrders] = useState(null);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    let alive = true;
    setOrders(null);
    const status = STATUS_TABS.find((t) => t.key === tab)?.value;
    api
      .get("/api/p2p/orders", { params: status ? { status, limit: 200 } : { limit: 200 } })
      .then(({ data }) => alive && (setOrders(data.orders || []), setError("")))
      .catch((e) => alive && (setError(e.response?.data?.detail || e.message), setOrders([])));
    return () => {
      alive = false;
    };
  }, [tab]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!orders || !q) return orders || [];
    return orders.filter(
      (o) =>
        o.order_id.toLowerCase().includes(q) ||
        String(o.fiat_amount).includes(q) ||
        (o.payment_method_snapshot?.type || "").toLowerCase().includes(q),
    );
  }, [orders, search]);

  return (
    <AppShell bare>
      <P2PPage>
        <P2PHeader subtitle="Every trade you've taken or posted — active, completed and historical." />

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <div className="flex max-w-full gap-1 overflow-x-auto rounded-lg border border-border bg-surface-2 p-1" data-testid="p2p-orders-status-tabs">
            {STATUS_TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                data-testid={`tab-${t.key}`}
                className={`whitespace-nowrap rounded-md px-3 py-1.5 text-xs transition ${
                  tab === t.key ? "brand-gradient text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search order ID, amount or method"
            data-testid="p2p-orders-search"
            className="w-full min-w-0 rounded-lg border border-border bg-surface-2 px-3 py-2 text-sm outline-none focus:border-primary sm:w-72"
          />
        </div>

        {error && <p className="mt-4 rounded-lg border border-down/40 bg-down/10 px-3 py-2 text-sm text-down">{error}</p>}

        <div className="mt-6 overflow-x-auto rounded-xl border border-border bg-surface/40" data-testid="p2p-orders-table">
          <div className="min-w-[760px]">
            <div className="table-head grid grid-cols-[1.2fr_80px_1fr_1fr_1fr_110px] gap-3 px-4 py-2.5">
              <span>Order</span>
              <span>Side</span>
              <span>Amount</span>
              <span>Method</span>
              <span>Opened</span>
              <span className="text-right">Status</span>
            </div>
            {orders === null ? (
              <div className="space-y-2 p-4">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="skeleton h-9 w-full" />
                ))}
              </div>
            ) : rows.length === 0 ? (
              <div className="px-4 py-14 text-center" data-testid="p2p-orders-empty">
                <p className="text-sm text-muted-foreground">
                  {tab === "all" ? "No P2P orders yet." : `Nothing in “${STATUS_TABS.find((t) => t.key === tab)?.label}” right now.`}
                </p>
                <Link to="/p2p" className="mt-3 inline-block text-sm text-primary hover:underline">
                  Browse offers
                </Link>
              </div>
            ) : (
              rows.map((o) => {
                const buyer = o.buyer_id === user?.id;
                const st = STATUS[o.status] || { label: o.status, tone: "bg-surface-2 text-muted-foreground" };
                return (
                  <Link
                    key={o.order_id}
                    to={`/p2p/orders/${o.order_id}`}
                    data-testid={`order-row-${o.order_id}`}
                    className="terminal-row grid-cols-[1.2fr_80px_1fr_1fr_1fr_110px] gap-3 px-4 py-3 text-sm"
                  >
                    <span className="num truncate text-muted-foreground">{o.order_id}</span>
                    <span className={buyer ? "text-up" : "text-down"}>{buyer ? "Buy" : "Sell"}</span>
                    <span className="num">
                      ₹{fmtINR(o.fiat_amount)}
                      <span className="block text-xs text-muted-foreground">
                        {Number(o.crypto_amount).toFixed(4)} {o.asset}
                      </span>
                    </span>
                    <span className="text-muted-foreground">{o.payment_method_snapshot?.type || "—"}</span>
                    <span className="num text-muted-foreground">{fmtDate(o.created_at)}</span>
                    <span className={`justify-self-end rounded-md px-2 py-0.5 text-xs ${st.tone}`}>{st.label}</span>
                  </Link>
                );
              })
            )}
          </div>
        </div>
      </P2PPage>
    </AppShell>
  );
}
